import { Box, Input } from '@mui/material';
import { styled } from '@mui/material/styles';
import { migrateMathStrings } from '@tiptap/extension-mathematics';
import type { Node as PMNode } from '@tiptap/pm/model';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AsyncActionStatus } from '../../../../actions/common.actionTypes';
import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import { getConversationStream } from '../../../../reducers/aiAssistant.selectors';
import services from '../../../../services';
import {
	AIAssistantEditorMode,
	aiAssistantEditorContext,
} from '../../../../state/AIAssistantEditorProvider';
import bugsnagClient from '../../../../utils/bugsnagClient';
import { TipTapExtensionKit } from '../../../../utils/tipTapEditor';
import useScreenSize from '../../../../utils/useScreenSize';
import MathEditorDialog from '../../../MathEditorDialog/MathEditorDialog';
import LinkMenuWrapper from '../../../RichTextEditor/LinkMenu/LinkMenuWrapper';
import TableInsertDialog from '../../../TableInsertDialog';
import { ACCEPTED_IMAGE_TYPES_STRING } from '../constants';
import { AIAssistantTextEditorToolbar } from './index';
import { useImageUpload } from './useImageUpload';

const MathEditorErrorBoundary =
	bugsnagClient?.getPlugin?.('react')?.createErrorBoundary?.(React) ??
	(({ children }: { children: React.ReactNode }) => <>{children}</>);

interface StyledEditorContentProps {
	editoremptyplaceholder: string;
	lineemptyplaceholder: string;
}

const StyledEditorContent = styled(EditorContent)<StyledEditorContentProps>(
	({ editoremptyplaceholder, lineemptyplaceholder }) => ({
		'& .ProseMirror': {
			outline: 'none',
			minHeight: '200px',
			padding: spaces.md,
		},
		'& .ProseMirror:focus': { outline: 'none' },
		'& .tiptap p.is-editor-empty:first-of-type::before, & .tiptap p.is-empty::before': {
			opacity: 0.5,
			float: 'left',
			height: 0,
			pointerEvents: 'none',
		},
		'& .tiptap p.is-editor-empty:first-of-type::before': {
			content: `"${editoremptyplaceholder}"`,
		},
		'& .tiptap p.is-empty::before': {
			content: `"${lineemptyplaceholder}"`,
		},
		'& .tiptap blockquote': {
			borderLeft: `3px solid ${palette.primary.main}`,
			margin: '1rem 0',
			paddingLeft: '1rem',
			fontStyle: 'italic',
			color: palette.text.secondary,
		},
		'& .tiptap code': {
			backgroundColor: palette.background.paperElevation2,
			borderRadius: '3px',
			padding: '0.2em 0.4em',
			fontSize: '0.9em',
			fontFamily: 'monospace',
		},
		'& .tiptap pre': {
			backgroundColor: palette.background.paperElevation2,
			borderRadius: '4px',
			padding: spaces.md,
			margin: '0.5rem 0',
			overflow: 'auto',
		},
		'& .tiptap pre code': {
			backgroundColor: 'transparent',
			padding: 0,
			fontSize: '0.875rem',
			fontFamily: 'monospace',
		},
		'& .tiptap ul, & .tiptap ol': {
			padding: '0 1rem',
			margin: '0.5rem 0',
		},
		'& .tiptap ul[data-type="taskList"]': {
			listStyle: 'none',
			padding: 0,
		},
		'& .tiptap ul[data-type="taskList"] li': {
			display: 'flex',
			alignItems: 'flex-start',
		},
		'& .tiptap ul[data-type="taskList"] li > label': {
			marginRight: '0.5rem',
			userSelect: 'none',
		},
		'& .tiptap ul[data-type="taskList"] li > div': {
			flex: 1,
		},
		'& .tiptap img': {
			maxWidth: '100%',
			height: 'auto',
			borderRadius: '4px',
			margin: '0.5rem 0',
		},
		'& .tiptap img.ProseMirror-selectednode': {
			outline: `3px solid ${palette.primary.main}`,
		},
		'& .tiptap table': {
			borderCollapse: 'collapse',
			tableLayout: 'fixed',
			width: '100%',
			margin: '1rem 0',
			overflow: 'hidden',
		},
		'& .tiptap table td, & .tiptap table th': {
			minWidth: '1em',
			border: `1px solid ${palette.divider}`,
			padding: '8px',
			verticalAlign: 'top',
			boxSizing: 'border-box',
			position: 'relative',
		},
		'& .tiptap table th': {
			fontWeight: 'bold',
			textAlign: 'left',
			backgroundColor: palette.background.paperElevation2,
		},
		'& .tiptap table .selectedCell': {
			backgroundColor: palette.action.selected,
		},
		'& .tiptap table .column-resize-handle': {
			position: 'absolute',
			right: '-2px',
			top: 0,
			bottom: '-2px',
			width: '4px',
			backgroundColor: palette.primary.main,
			pointerEvents: 'none',
		},
	})
);

interface AIAssistantTextEditorContentProps {
	content: string;
	onContentChange: (content: string) => void;
}

const AIAssistantTextEditorContent: React.FC<AIAssistantTextEditorContentProps> = ({
	content,
	onContentChange,
}) => {
	const { t } = useTranslation();
	const { textEditorRef, mode, markContentChanged } = useContext(aiAssistantEditorContext);
	const conversationStream = useSelector(getConversationStream);
	const previousContentRef = useRef<string>('');
	const isUpdatingFromPropRef = useRef<boolean>(false);
	const onContentChangeRef = useRef(onContentChange);
	const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
	const [isMathEditorDialogOpen, setIsMathEditorDialogOpen] = useState(false);
	const [mathEditorState, setMathEditorState] = useState<{
		latex: string;
		pos: number;
		isBlock: boolean;
	}>({
		latex: '',
		pos: 0,
		isBlock: false,
	});
	const editorRef = useRef<Editor | null>(null);
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isXsOrSmScreen = isXsScreen || isSmScreen;

	const handleMarkContentChanged = useCallback(() => {
		if (isXsOrSmScreen) {
			markContentChanged();
		}
	}, [isXsOrSmScreen, markContentChanged]);

	// Detect if streaming is currently active for STREAM mode
	const isStreamMode = mode === AIAssistantEditorMode.STREAM;
	const isActiveStreaming = conversationStream.status === AsyncActionStatus.LOADING && isStreamMode;

	// Keep the callback ref updated
	useEffect(() => {
		onContentChangeRef.current = onContentChange;
	}, [onContentChange]);

	const uploadImage = useCallback(async (file: File): Promise<{ displayUrl: string }> => {
		const formData = new FormData();
		formData.append('image', file);
		const response = await services.ai.uploadImage(formData);
		return response as { displayUrl: string };
	}, []);

	const { fileInputRef, handleImageUploadClick, handleFileChange } = useImageUpload({
		editor: editorRef.current,
		uploadImage,
	});

	const handleMathClick = useCallback((node: PMNode, pos: number, isBlock: boolean) => {
		const latex = node.attrs.latex || '';
		setMathEditorState({ latex, pos, isBlock });
		setIsMathEditorDialogOpen(true);
	}, []);

	const config = {
		ai: {
			openGenerateWorksheetModal: () => {}, // No-op for AI Assistant text editor
			openTableInsertDialog: () => setIsTableDialogOpen(true),
			openImageUploadDialog: handleImageUploadClick,
		},
		mathEditor: {
			onInlineMathClick: (node: PMNode, pos: number) => handleMathClick(node, pos, false),
			onBlockMathClick: (node: PMNode, pos: number) => handleMathClick(node, pos, true),
		},
		disabledSlashCommands: ['ai'],
	};

	const editor: Editor | null = useEditor(
		{
			editable: true,
			content: content,
			extensions: TipTapExtensionKit(config),
			editorProps: {
				attributes: {
					id: 'ai-assistant-text-editor',
					autocomplete: 'off',
					autocorrect: 'off',
					autocapitalize: 'off',
				},
			},
			onCreate: ({ editor: ed }) => {
				// Convert any remaining $...$ text patterns to proper math nodes
				migrateMathStrings(ed);
			},
		},
		[isActiveStreaming]
	);

	useEffect(() => {
		if (!editor) return;

		const handleUpdate = () => {
			// Prevent circular updates when we're updating from props
			if (isUpdatingFromPropRef.current) {
				return;
			}
			const newContent = editor.getHTML();
			onContentChangeRef.current(newContent);
		};

		const handleFocus = () => {
			handleMarkContentChanged();
		};

		editor.on('update', handleUpdate);
		editor.on('focus', handleFocus);

		return () => {
			editor.off('update', handleUpdate);
			editor.off('focus', handleFocus);
		};
	}, [editor, handleMarkContentChanged]);

	useEffect(() => {
		if (!editor || !content || isActiveStreaming) return;

		const editorContent = editor.getHTML();
		const previousContent = previousContentRef.current;

		if (content !== editorContent) {
			// Set flag to prevent circular updates
			isUpdatingFromPropRef.current = true;

			try {
				// Save current cursor position
				const { from, to } = editor.state.selection;

				// If content is longer and starts with the previous content, we can append incrementally
				// Note: previousContent must be non-empty to use incremental append, otherwise do full replace
				// (every string starts with empty string, so startsWith('') is always true)
				if (
					previousContent &&
					content.length > previousContent.length &&
					content.startsWith(previousContent)
				) {
					// Extract only the new content
					const newContentPortion = content.slice(previousContent.length);

					// Move cursor to the end and insert new content
					const endPosition = editor.state.doc.content.size;
					editor.commands.insertContentAt(endPosition, newContentPortion, {
						updateSelection: false,
					});
				} else {
					// Full replace + cursor restore in ONE transaction. A separate
					// setTextSelection dispatch lets the slash-command suggestion activate on
					// the replaced content (cursor lands at the end of a paragraph starting
					// with "/") and deactivate again before its async onStart ran → onExit crash.
					editor
						.chain()
						.setContent(content, false)
						.command(({ tr, commands }) => {
							const maxPos = tr.doc.content.size;
							return from <= maxPos && to <= maxPos ? commands.setTextSelection({ from, to }) : true;
						})
						.run();
				}

				previousContentRef.current = content;
			} finally {
				// Reset flag after a small delay to allow the update to complete
				setTimeout(() => {
					isUpdatingFromPropRef.current = false;
				}, 0);
			}
		}
	}, [content, editor, isActiveStreaming]);

	// Update context ref when editor changes
	useEffect(() => {
		editorRef.current = editor;
		if (textEditorRef) {
			textEditorRef.current = editor;
		}
		return () => {
			editorRef.current = null;
			if (textEditorRef) {
				textEditorRef.current = null;
			}
		};
	}, [editor, textEditorRef]);

	if (!editor) return null;

	return (
		<>
			{!isXsOrSmScreen && <AIAssistantTextEditorToolbar editor={editor} />}
			<Box
				data-testid="text-editor-content"
				sx={{
					flex: 1,
					overflow: 'auto',
					paddingX: isXsOrSmScreen ? spaces.xl : 0,
				}}
			>
				<StyledEditorContent
					editor={editor}
					editoremptyplaceholder={t('Click here to write...')}
					lineemptyplaceholder={t('Type / to browse options')}
				/>
			</Box>
			<LinkMenuWrapper editor={editor} />
			<TableInsertDialog
				open={isTableDialogOpen}
				onClose={() => setIsTableDialogOpen(false)}
				editor={editor}
			/>
			<MathEditorErrorBoundary>
				<MathEditorDialog
					open={isMathEditorDialogOpen}
					onClose={() => setIsMathEditorDialogOpen(false)}
					editor={editor}
					initialLatex={mathEditorState.latex}
					pos={mathEditorState.pos}
					isBlock={mathEditorState.isBlock}
				/>
			</MathEditorErrorBoundary>
			{!isXsOrSmScreen && (
				<Input
					data-testid="file-input"
					sx={{ display: 'none' }}
					inputRef={fileInputRef}
					type="file"
					inputProps={{ accept: ACCEPTED_IMAGE_TYPES_STRING }}
					onChange={handleFileChange}
				/>
			)}
		</>
	);
};

export default AIAssistantTextEditorContent;
